import {Component, Injector, ViewEncapsulation, SimpleChanges, Input, OnChanges, Output, signal} from '@angular/core';
import {GeneralPortfolioComponent} from "../general-portfolio/general-portfolio.component";
import { HC_COLORS } from '../../charts/highcharts-shared';
import { ColumnType, CimDatatableColumn, CimDatatableComponent } from "../../components/cim-datatable/cim-datatable.component";
import {
  EvaluationData,
  OptimPerformanceItem,
  OptimWeightItem,
} from '../../shared/models/portfolio-analytics.model';
import {CompanyModel} from "../../shared/models/company.model";
import { Subscription } from 'rxjs';
import { forkJoin, of } from 'rxjs';
import moment from "moment";
import { MaterialModule } from "src/app/app.materials";
import { RouterModule } from '@angular/router';
import { InfoComponent } from "src/app/components/info/info.component";
import { CimDatepickerComponent } from "src/app/components/cim-datepicker/cim-datepicker.component";
import { OptimizationSectionsComponent } from "src/app/components/optimization-sections/optimization-sections.component";
import { ChartPlaceholderComponent } from "src/app/components/chart-placeholder/chart-placeholder.component";
import { ViewsSetterComponent } from "src/app/components/views-setter/views-setter.component";
import { OptimizationViewsPanelComponent } from "src/app/components/optimization-views-panel/optimization-views-panel.component";
import { ConstraintSetterComponent } from "src/app/components/constraint-setter/constraint-setter.component";
import { EfficientFrontierChartComponent } from "src/app/charts/efficient-frontier-chart/efficient-frontier-chart.component";
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PdfExportDialogComponent } from 'src/app/components/pdf-export-dialog/pdf-export-dialog.component';

enum EfficientType {
  Constrainted = 'optim_efficient_frontier',
  Unconstrainted = 'optim_efficient_frontier_unconstrained'
}

@Component({
  selector: 'app-optimization',
  templateUrl: './optimization.component.html',
  styleUrls: [
    '../overview/portfolio-summary/summary-cards/summary-cards.component.scss',
    './optimization.component.scss'
  ],
  encapsulation: ViewEncapsulation.None,
  imports: [MaterialModule, RouterModule, InfoComponent, CimDatepickerComponent, OptimizationSectionsComponent, ChartPlaceholderComponent, OptimizationViewsPanelComponent, ConstraintSetterComponent, EfficientFrontierChartComponent, CommonModule, FormsModule, CimDatatableComponent],
  standalone: true,
})
export class OptimizationComponent extends GeneralPortfolioComponent {
  isPrint = false;
  efficientFrontierType = EfficientType.Constrainted;
  cardExporting: Record<string, boolean> = {};

  @Input() optim : Subscription;
  tickers = [];
  correlationMatrixData: any | null = null;
  covarianceMatrixData: any | null = null;
  correlationMatrixTableData: any | null = null;
  covarianceMatrixTableData: any | null = null;
  covarianceMatrixBlacklitterTableData: any | null = null;

  sliderVolatility: number = 0;

  evaluationData: EvaluationData = {};
  // Array extended with named string-keyed slots (e.g. optimPerformance['optim_performance'])
  optimPerformance: any = [];
  optimWeights: OptimWeightItem[] = [];
  filteredOptimWeights: OptimWeightItem[] = [];

  dataLoaded = signal(false);
  viewTablesLoaded = signal(false);
  // True while an optimization run is computing in the background. Drives the
  // disabled state + inline spinner on every "Optimize" button so the user is
  // not blocked but is clearly told a run is in progress.
  optimizing = signal(false);

  filteredForecastedReturnsTableData: OptimWeightItem[] = [];

  top3Holdings: OptimWeightItem[] = [];
  top3LargestDev: OptimWeightItem[] = [];
  benchmarkReturn: any = [];

  minVolatility: number = 0;
  maxVolatility: number = 100;

  volatilityLine: number | string = 0;
   lineLabels = {
        efficient_portfolio_max_sharpe_ratio: 'Tangency PF',
        efficient_portfolio_given_mu: 'Selected portfolio', // Target Return PF
        efficient_portfolio_given_sigma: 'Target Risk PF',
        baseline_portfolio: 'Baseline PF',
        efficient_portfolio_max_mu: 'Maximum Return',
        minimum_variance_portfolio: 'Minimum Risk',
        next_best_trade: 'Custom Trade Portfolio'
    };

  evaluationDataLoaded: boolean = false;

  forecastedReturnsTableColumns: CimDatatableColumn[] = [
    {id: 'company_name', label: 'Asset', type: ColumnType.CompanyInfo},
    {id: 'Weight', label: 'Weights', type: ColumnType.Percentage},
    {id: 'weight_marketcap', label: 'Marketcap Weights', type: ColumnType.Percentage},
    // {id: 'Ticker', label: 'Ticker', type: ColumnType.Simple, inputDisabled: true, canHide: true},
    {id: 'volatility', label: 'Volatility', type: ColumnType.Percentage},
    {id: 'historical_returns', label: 'Historical Returns', type: ColumnType.Percentage},
    /*{id: 'implied_returns', label: 'Implied Returns', type: ColumnType.Number2Decimal, suffix: '%'},*/
    {id: 'market_equilibrium', label: 'Market Equilibrium Returns', type: ColumnType.Percentage},
    // {id: 'forecasted_returns', label: 'Forecasted Return', type: ColumnType.Input, suffix: '%'},
    {id: 'implied_returns', label: 'Implied Returns', type: ColumnType.Percentage},
    {id: 'mkt_eq_returns', label: 'Implied vs Mkt. Eq. Returns', type: ColumnType.Percentage},
  ];

  footerData: Record<string, string> = {
    company_name: 'Total expected return',
    Ticker: '',
    Weight: '',
    weight_marketcap: '',
    volatility: '',
    historical_returns: 'foot',
    market_equilibrium: 'foot',
    forecasted_returns: 'foot',
  }

  filter: Record<string, string | null> = {
    sector: null,
    country: null,
    type: null
  }

  filterLists: any = {
    sectorList: [],
    countryList: [],
    typeList: [],
  }

  companyReturns: any = {};

  historicalReturnTableData: any[] = [];
  filteredHistoricalReturnTableData: any[] = [];
  historicalReturnTableColumns: any[] = [
    {id: 'company_name', label: 'Asset', type: ColumnType.CompanyInfo},
    {id: 'volatility', label: 'Volatility', type: ColumnType.Percentage},
    {id: 'historical_returns', label: 'Historical Returns', type: ColumnType.Percentage},
    {id: 'implied_returns', label: 'Implied Returns', type: ColumnType.Percentage},
    {id: 'market_equilibrium', label: 'Market Equilibrium Returns', type: ColumnType.Percentage},
    {id: 'forecasted_returns', label: 'Combined Returns', type: ColumnType.Percentage, bold: true},
  ];

  marketEquilibriumTableData: any[] = [];
  filteredMarketEquilibriumTableData: any[] = [];
  marketEquilibriumTableColumns: any[] = [
    {id: 'company_name', label: 'Asset', type: ColumnType.CompanyInfo},
    {id: 'Ticker', label: 'Ticker', type: ColumnType.Simple},
    {id: 'Weight', label: 'Weight', type: ColumnType.Percentage},
    {id: 'weight_marketcap', label: 'Marketcap Weights', type: ColumnType.Percentage},
    {id: 'implied_returns', label: 'Market Equilibrium Returns', type: ColumnType.Percentage},
  ];

  forecastedReturnsTableData: any[] = [];

  resultsTableData: any[] = [];
  resultsTableDataFooter: any[] = [];
  resultsTableColumns: any[] = [
    {id: 'companyName', label: 'Asset', type: ColumnType.CompanyInfo},
    {id: 'expectedReturn', label: 'Expected Returns', type: ColumnType.Percentage},
    {id: 'historicalReturn', label: 'Historical Returns', type: ColumnType.Percentage},
    {id: 'volatility', label: 'Volatility', type: ColumnType.Percentage},
    {id: 'sharpe', label: 'Expected Sharpe', type: ColumnType.Number2Decimal},
    {id: 'allocation', label: 'Allocation', type: ColumnType.Percentage},
    {id: 'minWeight', label: 'Min weight', type: ColumnType.Percentage},
    {id: 'maxWeight', label: 'Max weight', type: ColumnType.Percentage},
  ];


  optimizedPortfolioWeightsColumns = [
    {id: 'company_name', label: 'Asset', type: ColumnType.CompanyInfo},
    {id: 'Ticker', label: 'Ticker', type: ColumnType.Simple},
    {id: 'minimum_variance_portfolio', label: 'Minimum Variance Portfolio', type: ColumnType.Percentage},
    {id: 'efficient_portfolio_max_mu', label: 'Efficient Portfolio Max Return', type: ColumnType.Percentage},
    {id: 'efficient_portfolio_max_sharpe_ratio', label: 'Efficient Portfolio Max Sharpe', type: ColumnType.Percentage},
    {id: 'efficient_portfolio_given_mu', label: 'Efficient Portfolio Target Return', type: ColumnType.Percentage},
    {id: 'efficient_portfolio_given_sigma', label: 'Efficient Portfolio Target Volatility', type: ColumnType.Percentage},
  ];
  constraintLists = {
    assets: [],
    sectors: [],
    countries: [],
    benchmarks: []
  }

  impliedReturnColumns = [
    {id: 'company_name', label: 'Asset', type: ColumnType.CompanyInfo},
    {id: 'Ticker', label: 'Ticker', type: ColumnType.Simple},
    {id: 'Weight', label: 'Weight', type: ColumnType.Percentage},
    {id: 'implied_ret', label: 'Implied Returns', type: ColumnType.Percentage},
  ];
  impliedReturnData = [];
  filteredImpliedReturnData = [];
  portfolioImpliedReturn = 0;
  portfolioMarketEquilibriumReturn = 0;

  viewReturns: any = {}

  efficientOptimWeights:any[] = [];
  typeList = [];
  portfolioHasETF = false;

  selectedPerformanceType = '';
  

  constructor(injector: Injector) {
    super(injector);
    document.addEventListener("scroll", (event) => {
      this.getClosestCard();
    });
    this.service.optim$.subscribe(optim => this.reloadPf(optim));
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.service.benchmark$.subscribe((bench: any) => {
      if (bench && bench.id && this.optimizationData?.historical_returns) {
        this.initEfficientChart();
      }
    });
  }

  reloadPf(optim){
    this.optim = optim;

    if(this.portfolio.symbol_performance && this.evaluationData?.risk_contribution){
      this.setGeneralData();
    }

    if(this.resultsTableData.length > 0){
      this.initViewTables();
    }
    this.initEfficientChart();
  }
  setGeneralData() {
    this.optim = (this.optim && this.optim.toString() !== '')
      ? this.optim.toString()
      : this.optimizationData.optim_parameters.optim_performance;
    this.optimName = this.lineLabels[this.optim.toString()];

    this.portfolio.symbol_performance.map((e: any) => {
      if (e.Ticker !== 'Portfolio' && e.Weight > 0) {
        let evalData = this.evaluationData.risk_contribution.find(a => a.Ticker == e.Ticker && a.Portfolio == this.optim.toString());

        let forecastedReturns = this.optimizationData.forecasted_returns.find(a => a.Ticker == e.Ticker && e.Weight > 0).forecasted_returns;
      }
    });

  }

 
  getClosestCard() {
    const cards: any = document.getElementsByClassName('settings');
    const underContainer = document.getElementsByClassName('under-container')[2];
    if (!underContainer) {
      return;
    }
    const navs = underContainer.children;
    let closest = 0;
    let selected = cards[0];
    for(let counter = 0; counter < cards.length; counter++) {
      if(window.scrollY > cards[counter].offsetTop) {
        selected = cards[counter];
        closest = counter;
      }
      if(navs[counter]) {
        navs[counter].className = '';
      }
    }
    if (navs[closest]) {
      navs[closest].className = 'active';
    }
  }

  initPage() {
    this.typeList = this.service.typeList;
    this.portfolioHasETF = this.portfolio.hasETF();
    
    // need the performance data for efficient frontier
    if (this.portfolio && this.portfolio.id) {
      if (!this.benchmark && this.portfolio.benchmark) {
        
        this.service.getBenchmark(btoa(this.portfolio.benchmark), this.portfolio.id).subscribe((resp: any) => {
          this.service.selectBenchmark(resp);
          this.selectedBenchmark = resp;
        });
      }

      this.fetchPlotData()
      this.fetchBenchmarks();
      this.fetchBenchmarksList();
    }
    this.renameEvaluationTypesInPerformance();
    this.initGroupConstraintLists();
    
  }
  async fetchBenchmarks() {
    var resp = await this.service.getBenchmarks();
    resp.subscribe((res: any) => {
      if (res) {
        this.benchmarks = res;
        this.constraintLists.benchmarks = res;
      }
    });
  }

  fetchPlotData() {
    forkJoin({
      a: this.service.fetchOptimizationData(this.portfolio.id),
      c: this.service.fetchPerformancePlot(this.portfolio.id),
      v: this.service.fetchEvaluationData(this.portfolio.id),
    }).subscribe(val => {
      this.optimizationData = val.a;
      this.evaluationData = val.v;
      this.plotData = this.convertToPercentages(val.c);

      this.optim = val.a.optim_parameters?.optim_performance ?? '';
      this.optimName = this.lineLabels[this.optim.toString()] ?? '';
      // Set default values
      if (!this.optimizationData.optim_parameters.assumption_return_type) {
        this.optimizationData.optim_parameters.assumption_return_type = 'forecasted';
      }
      if (!this.optimizationData.optim_parameters.correlation_frequency) {
        this.optimizationData.optim_parameters.correlation_frequency = 'daily';
      }
      if (!this.optimizationData.optim_parameters.correlation_method) {
        this.optimizationData.optim_parameters.correlation_method = 'forecasted';
      }
      if (!this.optimizationData.optim_parameters.assumption_horizon) {
        this.optimizationData.optim_parameters.assumption_horizon = 12;
      }

      if (!this.optimizationData.optim_parameters.return_start_date) {
        this.optimizationData.optim_parameters.return_start_date = this.portfolio.start_date;
      }

      this.afterOptimizationDateReceived();
      this.evaluationDataLoaded = true;
      if (this.portfolio?.symbol_performance && this.evaluationData?.risk_contribution) {
        this.setGeneralData();
      }
    });
  }
  beforeViewTablesLoaded() {
    this.footerData.historical_returns = `${this.viewReturns.historical || 0}%`;
    this.footerData.implied_returns = `${this.viewReturns.implied || 0}%`;
    this.footerData.market_equilibrium = `${this.viewReturns.market || 0}%`;
    this.footerData.forecasted_returns = `${this.viewReturns.forecast || 0}%`;

    this.initTableFilters();
  }

  initTableFilters() {
    this.filteredForecastedReturnsTableData = this.forecastedReturnsTableData.map((e) => {
      return {
        ...e,
        mkt_eq_returns: (e.implied_returns - e.market_equilibrium).toPrecision(4),
      }
    });;
    this.filter = {
      sector: null,
      country: null,
      type: null
    }

    let sectors = [];
    let country = [];
    let type = [];

    this.forecastedReturnsTableData.map(d => {
      sectors.push(d.sector);
      country.push(d.country);
      type.push(d.type);
    })

    this.filterLists = {
      sectorList: [...new Set(sectors)],
      countryList: [...new Set(country)],
      typeList: [...new Set(type)],
    }
  }

  reloadHistoricalTable() {
    this.viewTablesLoaded.set(false);
    this.historicalReturnTableData = [];
    this.marketEquilibriumTableData = [];
    this.forecastedReturnsTableData = [];
    this.impliedReturnData = [];
    this.service.fetchOptimizationData(this.portfolio.id, true).subscribe((resp: any) => {
      this.optimizationData = resp;
      this.initViewTables();
    })
  }


  extendListWithCompanyData(list = [], name, returnName) {
    let result = [];
    list.map((d: any) => {
      d.return = +(d[returnName]*100).toFixed(2);

      if (d.Ticker === 'Portfolio') {
        this.viewReturns[name] = d.return;
      } else {
        
        if (!this.companyReturns[d.Ticker]) {
          this.companyReturns[d.Ticker] = {}
        }
        this.companyReturns[d.Ticker][name] = d.return;
        d.company_name = this.portfolio.security_overviews[d.Ticker] ? this.portfolio.security_overviews[d.Ticker].company_name : this.getFundName(d.Ticker);
        result.push(d);
      }
    })
    return result
  }



  initViewTables() {
    this.viewTablesLoaded.set(false);
    this.companyReturns = {};
     this.resultsTableData= [];
     this.allocation=[];

    let baselinePorfio = {}; 

    this.optimPerformance.push(baselinePorfio);
    var optim = this.optim ? this.optim.toString() : this.optimizationData.optim_parameters.optim_performance;
    for(let prop of ['optim_performance', "optim_performance_unconstrained", "optim_performance_cost_of_constrained"]){
          this.optimPerformance[prop] = this.portfolio[prop].filter(d => d.view_name === 'forecasted_returns' && d.Portfolio === optim) ;

    }

    // For the saved Next Best Trade view, optim_weights items don't have a
    // next_best_trade field; fall back to modified_optim_weights which is
    // populated by the /next-best-trade/apply endpoint.
    const nbtWeightByTicker: Record<string, number> = {};
    if (optim === 'next_best_trade' && Array.isArray(this.optimizationData?.modified_optim_weights)) {
      for (const w of this.optimizationData.modified_optim_weights) {
        if (w?.source === 'next_best_trade' && w?.ticker != null) {
          nbtWeightByTicker[w.ticker] = w.weight;
        }
      }
    }

    if(optim == 'baseline_portfolio'){
      let expectedReturn = this.optimizationData.forecasted_returns.filter(d => d.Ticker == 'Portfolio')[0].forecasted_returns;
      let volatility = this.optimizationData.historical_returns.filter(d => d.Ticker == 'Portfolio')[0].volatility;
      let sharpe = 0; 
      if(volatility > 0){
        sharpe = expectedReturn / volatility;
      }
      
       baselinePorfio = {
        'Expected_Return' : expectedReturn,
        'Std_Deviation' : volatility,
        'Sharpe_Ratio': sharpe,
        'view_name': 'forecasted_returns',
        'Portfolio': 'baseline_portfolio'

      }
      this.optimPerformance['optim_performance'].push( baselinePorfio);
    }

    this.historicalReturnTableData = this.extendListWithCompanyData(this.optimizationData.historical_returns, 'historical', 'historical_returns');
    this.marketEquilibriumTableData = this.extendListWithCompanyData(this.optimizationData.market_equilibrium, 'market', 'implied_ret');
    this.forecastedReturnsTableData = this.extendListWithCompanyData(this.optimizationData.forecasted_returns, 'forecast', 'forecasted_returns');
    this.impliedReturnData = this.extendListWithCompanyData(this.optimizationData.implied_returns, 'implied', 'implied_ret');

    this.extendListWithCompanyData(this.optimizationData.historical_returns, 'volatility', 'volatility');
    this.extendListWithCompanyData(this.optimizationData.market_equilibrium, 'weight_marketcap', 'weight_marketcap');

    this.forecastedReturnsTableData = this.forecastedReturnsTableData.map((d: any) => {
      if (!d.converted) {
        d.forecasted_returns = +(d.forecasted_returns)// multiple with 100 to get percenteges in input and table
        d.historical_returns = this.companyReturns[d.Ticker]['historical']/100;
        d.volatility = this.companyReturns[d.Ticker]['volatility']/100;
        d.weight_marketcap = this.companyReturns[d.Ticker]['weight_marketcap']/100;
        d.market_equilibrium = this.companyReturns[d.Ticker]['market']/100;
        d.implied_returns = this.companyReturns[d.Ticker]['implied']/100;
        d.sector = this.portfolio.security_overviews[d.Ticker] && this.portfolio.security_overviews[d.Ticker].sector ? this.portfolio.security_overviews[d.Ticker].sector.name : '';
        d.country = this.portfolio.security_overviews[d.Ticker] && this.portfolio.security_overviews[d.Ticker].country ? this.portfolio.security_overviews[d.Ticker].country.name : '';
        d.type = this.portfolio.security_overviews[d.Ticker] ? this.portfolio.security_overviews[d.Ticker].security_type_equity : '';
        d.converted = true;
        d.allocation = this.optimWeights.map((v) => {

          if (v.Ticker == d.Ticker && v.view_name == 'forecasted_returns') {
            if(optim === 'efficient_portfolio_max_sharpe_ratio')
              return v.efficient_portfolio_max_sharpe_ratio;
            if(optim === 'efficient_portfolio_max_mu')
              return v.efficient_portfolio_max_mu;
            if(optim == 'minimum_variance_portfolio')
              return v.minimum_variance_portfolio;
            if(optim === 'efficient_portfolio_given_mu')
              return v.efficient_portfolio_given_mu
            if(optim === 'efficient_portfolio_given_sigma')
              return v.efficient_portfolio_given_sigma
            if(optim === 'baseline_portfolio')
              return this.evaluationData.risk_contribution.find(a => a.Ticker == d.Ticker && a.Portfolio == optim).weight
            if(optim === 'next_best_trade')
              return nbtWeightByTicker[d.Ticker] ?? 0;
          } else {
            return false;
          }
        }).filter((e) => {
          return e !== false;
        })[0]
      }
      return d
    });
    this.filteredHistoricalReturnTableData = this.historicalReturnTableData.map((e) => {
      const ticker = this.forecastedReturnsTableData.filter((d) => d.Ticker === e.Ticker)[0];
      return {
        ...ticker,
        ...e,
        implied_returns: ticker.implied_returns ,
        market_equilibrium: ticker.market_equilibrium ,
        forecasted_returns: ticker.forecasted_returns ,
        mkt_eq_returns: (e.implied_returns - e.market_equilibrium)/100
      }
    });
    this.resultsTableData = [];
    this.resultsTableDataFooter = [];
    this.optimizationData.forecasted_returns.map((data: any, index) => {
      let companyName = this.portfolio.security_overviews[data.Ticker] ? this.portfolio.security_overviews[data.Ticker].company_name : this.getFundName(data.Ticker);
      let constraintData = this.getConstraintForAsset(companyName);
      if(data.Ticker === 'Portfolio'){
        const perf = this.optimPerformance['optim_performance'][0];
        if (perf) {
          this.volatilityLine = (perf.Std_Deviation*100).toFixed(2);
          this.resultsTableDataFooter.push({
            companyName: companyName,
            expectedReturn: (perf.Expected_Return*100).toFixed(2) + "%",
            historicalReturn: (this.optimizationData.historical_returns[index].historical_returns*100).toFixed(2) + "%",
            volatility: (perf.Std_Deviation*100).toFixed(2) + "%",
            sharpe: (perf.Sharpe_Ratio).toFixed(2),
            allocation: "100%"
          });
        }
      }else{
        var allocation =  this.optimWeights.map((v) => {

            if (v.Ticker == data.Ticker && v.view_name == 'forecasted_returns') {
               if(optim === 'efficient_portfolio_max_sharpe_ratio')
                 return v.efficient_portfolio_max_sharpe_ratio;
               if(optim === 'efficient_portfolio_max_mu')
                 return v.efficient_portfolio_max_mu;
               if(optim == 'minimum_variance_portfolio')
                 return v.minimum_variance_portfolio;
               if(optim === 'efficient_portfolio_given_mu')
                 return v.efficient_portfolio_given_mu
               if(optim === 'efficient_portfolio_given_sigma')
                 return v.efficient_portfolio_given_sigma
               if(optim === 'baseline_portfolio')
                 return this.evaluationData.risk_contribution.find(a => a.Ticker == data.Ticker && a.Portfolio == optim).weight
               if(optim === 'next_best_trade')
                 return nbtWeightByTicker[data.Ticker] ?? 0;
            } else {
              return false;
            }
          }).filter((e) => {
            return e !== false;
          });
        this.allocation.push({'Ticker': data.Ticker, 'company_name': companyName, 'weight' : (allocation[0]*100).toFixed(2)});
        this.resultsTableData.push({
          companyName: companyName,
          Ticker: data.Ticker,
          expectedReturn: (data.forecasted_returns),
          historicalReturn: this.optimizationData.historical_returns[index].historical_returns,
          volatility: this.optimizationData.historical_returns[index].volatility,
          sharpe: ((data.forecasted_returns)/this.optimizationData.historical_returns[index].volatility).toFixed(2),
          allocation: allocation,

          maxWeight: constraintData ? constraintData.max : 0,
          minWeight: constraintData ? constraintData.min : 0
        });
      }

    })
    this.filteredMarketEquilibriumTableData = [...this.marketEquilibriumTableData];
    this.filteredForecastedReturnsTableData = this.forecastedReturnsTableData.map((e) => {
      return {
        ...e,
        mkt_eq_returns: (e.implied_returns - e.market_equilibrium).toPrecision(4),
      }
    });
    this.filteredImpliedReturnData = [...this.impliedReturnData];

    this.optimizationData.market_equilibrium.map(d => {
      if (d.Ticker == 'Market Equilibrium') {
        this.portfolioMarketEquilibriumReturn = +(d.implied_ret*100).toFixed(2);
      }
    });
    if(this.filteredForecastedReturnsTableData.length > 2 && this.filteredForecastedReturnsTableData[0].weight == this.filteredForecastedReturnsTableData[1].weight == this.filteredForecastedReturnsTableData[2].weight){
      Object.assign(this.top3Holdings, this.filteredForecastedReturnsTableData.sort((a, b) => {return parseFloat(b.weight_marketcap) - parseFloat(a.weight_marketcap)}));
    }else{
      Object.assign(this.top3Holdings, this.filteredForecastedReturnsTableData.sort((a, b) => {return +(b.Weight ?? 0) - +(a.Weight ?? 0)}));
    }    
    Object.assign(this.top3LargestDev = this.filteredForecastedReturnsTableData.sort((a, b) => {return Math.abs(+b.mkt_eq_returns) - Math.abs(+a.mkt_eq_returns)}));
    this.onForecastedReturnInputChange();
    this.beforeViewTablesLoaded();

    this.viewTablesLoaded.set(true);
  }

  onForecastedReturnInputChange() {
    let portfolioReturn = 0;
    this.forecastedReturnsTableData.map(d => {
      portfolioReturn += d.Weight * d.forecasted_returns
    })
    this.viewReturns.forecast = +(portfolioReturn.toFixed(2));
  }

  saveInputsAndTriggerHistoricalCalc() {
    if (this.optimizing()) {
      return;
    }
    let optimParams: any = this.collectParamsForPost();
    optimParams.optim_parameters.action  = 'optimize';
    this.optimizing.set(true);
    this.service.saveOptimizationData(this.portfolio.id, optimParams).subscribe((resp: any) => {
      if (resp.errro) {
        this.optimizing.set(false);
        this.service.notificationService.open(resp.error)
        return;
      }

      // The optimize action makes the backend mark the portfolio status=1
      // atomically with the R trigger, so we only need to start polling here.
      // A separate saveItem(status=1) would race with R's final status=3 write
      // and could leave the row stuck at 1.
      this.startBackgroundOptimization();
    }, () => {
      this.optimizing.set(false);
    })
  }

  // Non-blocking optimization. Instead of a modal dialog + full-screen
  // "Calculating..." overlay, the user keeps the page (with the Optimize
  // buttons disabled + spinning) and gets a toastr now. When polling reports
  // the run is done we surface a persistent toastr with a Reload action rather
  // than force-reloading, so the user decides when to refresh.
  private startBackgroundOptimization() {
    this.portfolio.status = 1;
    this.portfolio.last_updated_at = '';
    this.service.notificationService.open(
      'Your optimization is running. We will let you know when the results are ready.'
    );

    this.portfolioPollingService.watchPortfolio(this.portfolio.id, {
      onStatusComplete: () => {
        this.optimizing.set(false);
        this.service.notificationService.openWithCallback(
          'Optimization finished. Reload to see the new results.',
          'Reload',
          () => window.location.reload()
        );
      },
      onStatusFailed: () => {
        this.optimizing.set(false);
        this.service.notificationService.openPersistent(
          'No feasible portfolio could be generated with the selected constraints. Please review and adjust the constraints.'
        );
      },
      pollingInterval: 6000,
      initialDelay: 10000,
      listenerKey: 'optimization-' + this.portfolio.id,
    });
  }

   afterOptimizationDateReceived() {
    this.initTabels();
    this.initBlacklitterMatrix();
    this.optimizationData.correlation_matrix.items = this.optimizationData.correlation_matrix.items.map((i: any) => {
      i.ticker1 = i.ticker1 ?? this.getTicker(i.Var1);
      i.ticker2 = i.ticker2 ?? this.getTicker(i.Var2);
      i.Var1 = this.getFundName(i.Var1, false);
      i.Var2 = this.getFundName(i.Var2, false);
      return i;
    });

    this.correlationMatrixTableData = this.extractHeatMapTableData(this.optimizationData.correlation_matrix.items, Math.sqrt(this.optimizationData.correlation_matrix.items.length));
    this.optimizationData.covariance_matrix.items = this.optimizationData.covariance_matrix.items.map((i: any) => {
      i.ticker1 = i.ticker1 ?? this.getTicker(i.Var1);
      i.ticker2 = i.ticker2 ?? this.getTicker(i.Var2);
      i.Var1 = this.getFundName(i.Var1, false);
      i.Var2 = this.getFundName(i.Var2, false);
      if (!i.converted) {
        i.value = i.value * 100;
        i.converted = true;
      }
      return i;
    });
    this.covarianceMatrixTableData = this.extractHeatMapTableData(this.optimizationData.covariance_matrix.items, Math.sqrt(this.optimizationData.covariance_matrix.items.length));
    
    this.initEfficientChart();
    this.initViewTables();

    setTimeout(() => { this.dataLoaded.set(true); this.efficientChartLoaded.set(true); });
  }

  fetchEvaluationData() {
    this.service.fetchEvaluationData(this.portfolio.id).subscribe((resp: any) => {
      this.evaluationData = resp;
      if (this.evaluationDataLoaded) {
        this.setGeneralData();
      }
      this.evaluationDataLoaded = true;
    });
  }

  initBlacklitterMatrix() {
    if (this.optimizationData.covariance_matrix_blacklitter && this.optimizationData.covariance_matrix_blacklitter.items) {
      this.optimizationData.covariance_matrix_blacklitter.items = this.optimizationData.covariance_matrix_blacklitter.items.map((i: any) => {
        if (!i.converted) {
          i.value = i.value * 100;
          i.converted = true;
        }
        return i;
      })
      this.covarianceMatrixBlacklitterTableData = this.extractHeatMapTableData(this.optimizationData.covariance_matrix_blacklitter.items, this.portfolio.securities.length);
    }
  }

  initGroupConstraintLists() {
    let sectors = [];
    let countries = [];
    if (this.portfolio && this.portfolio.id && this.portfolio.securities) {
      this.portfolio.securities.map((c: CompanyModel) => {
        if(c.security_type_equity === 'SHARE'){
          if (c.sector) {
            sectors.push(c.sector)
          }
          if (c.country) {
            countries.push(c.country)
          }
        }
      })
      this.constraintLists.sectors = this.util.keepUniqueObjectOfArray(sectors);
      this.constraintLists.countries = this.util.keepUniqueObjectOfArray(countries);
      this.constraintLists.benchmarks = this.benchmarks;
      const securityAssets = this.portfolio.securities.map((s: any) => {
        s.name = s.company_name;
        return s;
      });
      // Sub-portfolios are not part of `securities`; pull them from
      // `security_overviews` (same source as the Combined Returns table) so they
      // can be constrained too.
      this.constraintLists.assets = this.mergeAssetsWithSubPortfolios(securityAssets);
      this.constraintLists.assets.sort((a,b) => (a.name.toLowerCase() > b.name.toLowerCase()) ? 1 : ((b.name.toLowerCase() > a.name.toLowerCase()) ? -1 : 0));
    }

    if (this.portfolio && this.portfolio.id) {
      this.portfolio.securities.map((c: CompanyModel) => {
        // if (c.sector) {
        //   sectors.push({'id': 23, 'name': 'MutualFund'})
        // }
        // if (c.country) {
        //   countries.push({'id': 161, 'name': 'MutualFund'})
        // }
      })
       this.constraintLists.sectors = this.util.keepUniqueObjectOfArray(sectors);
       this.constraintLists.countries = this.util.keepUniqueObjectOfArray(countries);
       this.constraintLists.benchmarks = this.benchmarks;
     
      this.constraintLists.assets.sort((a,b) => (a.name.toLowerCase() > b.name.toLowerCase()) ? 1 : ((b.name.toLowerCase() > a.name.toLowerCase()) ? -1 : 0));
    }
  }

  /**
   * Appends sub-portfolio "assets" (sourced from security_overviews, where they
   * appear with security_type_equity === 'PORTFOLIO') to the given asset list,
   * deduped by id. Sub-portfolios are not present in portfolio.securities, so
   * without this they were missing from the constraint asset selector even
   * though they show up in the Combined Returns table.
   */
  private mergeAssetsWithSubPortfolios(assets: any[]): any[] {
    const overviews = this.portfolio?.security_overviews || {};
    const existingIds = new Set(assets.map((a: any) => String(a.id)));
    const subPortfolios = Object.keys(overviews)
      .filter((ticker: string) => overviews[ticker]?.security_type_equity === 'PORTFOLIO')
      .map((ticker: string) => {
        const o = overviews[ticker];
        // Keep the PF_<id> ticker as the constraint identifier; the backend
        // resolves sub-portfolio holdings by this ticker, not by numeric id.
        return {
          ...o,
          id: ticker,
          name: o.company_name || o.name || ticker,
          security_type_equity: 'PORTFOLIO',
        };
      })
      .filter((sub: any) => !existingIds.has(String(sub.id)));
    return [...assets, ...subPortfolios];
  }

  /**
   * Annualized benchmark volatility over the portfolio's [start_date, end_date]
   * window. Used by the EF scatter point so the benchmark dot's X coordinate
   * tracks the portfolio horizon, not the fixed last-year perfAnn snapshot.
   * Built from benchmark.cumulative_returns: convert to period returns over
   * the window, take the sample std, scale by sqrt(12).
   */
  private computeBenchmarkVolForPortfolioTimespan(): number {
    const cr: any[] = this.benchmark?.cumulative_returns;
    if (!Array.isArray(cr) || cr.length < 3) {
      return 0;
    }
    const startDate = this.optimizationData?.optim_parameters?.return_start_date ?? this.portfolio?.start_date;
    const endDate = this.optimizationData?.optim_parameters?.return_end_date ?? this.portfolio?.end_date;
    if (!startDate || !endDate) {
      return 0;
    }
    const startMs = new Date(startDate).valueOf();
    const endMs = new Date(endDate).valueOf();
    const inWindow = cr.filter((d: any) => {
      const t = typeof d.date === 'number' ? d.date : new Date(d.date).valueOf();
      return t >= startMs && t <= endMs;
    });
    // Shared formula with Backtest Risk snapshot and Annual chart benchmark
    // bar: daily returns from cumulative_returns -> sample variance ->
    // sqrt(variance * 252). Only the window differs across the three sites,
    // matching R's PerformanceAnalytics convention for daily-frequency data.
    return this.annualizedVolFromCumulative(inWindow) * 100;
  }

  extractEfficientFrontierBenchmarkScatterData() {
    let volatilityValue = this.computeBenchmarkVolForPortfolioTimespan();
    let returnValue = 0;
    const scatterData = [];

    if (!volatilityValue && this.benchmark?.perfAnn) {
      const lastPerfAnnItem = this.benchmark.perfAnn[this.benchmark.perfAnn.length - 1];
      if (lastPerfAnnItem) {
        volatilityValue = lastPerfAnnItem.volatility;
      }
    }
    // EF line Y uses the optimizer's annualized exp_ret and X uses annualized
    // sd_ret (sqrt(252)). The benchmark dot must use CAGR to share those axes
    // -- period totalReturn on multi-year windows produces values like 194%
    // that don't belong on an annualized-return axis.
    if (this.benchmarkReturn && this.benchmarkReturn.cagrY1) {
      returnValue = this.benchmarkReturn.cagrY1;
    }
    if (returnValue && volatilityValue) {
      let benchmarkName = this.benchmark.index_name_full ?? "Benchmark";
      scatterData.push(
        {
          name: 'Benchmark: ' + benchmarkName,
          company: 'Benchmark: ' + benchmarkName,
          color: HC_COLORS.warn,
          x: +(volatilityValue).toFixed(2),
          y: +(returnValue).toFixed(2),
          dataLabels:{align: "left"},
          return_index: 0,
          sharpeRatio: returnValue / volatilityValue
        }
      );  
    }
    return scatterData;
  }

  initEfficientChart() {
    // First, ensure cumulative_returns is available. If the benchmark arrived
    // raw (no cumulative_returns), calculateBenchmark builds them from
    // benchmarkFx. After this step cumulative_returns should be present in
    // all normal flows.
    if (this.benchmark && !this.benchmark.cumulative_returns) {
      this.calculateBenchmark(this.benchmark);
    }

    // Recompute benchmarkReturn on every invocation so the EF dot's Y value
    // (cagrY1) tracks the user's start_date / end_date. The original code
    // nested this inside the "!cumulative_returns" gate, which froze the Y
    // value at first-load values once the cache was warm.
    const windowStart = this.optimizationData?.optim_parameters?.return_start_date ?? this.portfolio?.start_date;
    const windowEnd = this.optimizationData?.optim_parameters?.return_end_date ?? this.portfolio?.end_date;
    if (this.benchmark?.cumulative_returns?.length && windowStart && windowEnd) {
      const portfolioStart = new Date(windowStart).valueOf();
      const portfolioEnd = new Date(windowEnd).valueOf();
      const dateOfTheYear = moment.utc(new Date(portfolioEnd)).startOf('year').unix() * 1000;
      const copy: any[] = structuredClone(
        this.benchmark.cumulative_returns.filter((e: any) => e.date >= portfolioStart && e.date <= portfolioEnd)
      );
      if (copy.length >= 2 && copy[0].value !== 0) {
        const lastVal = copy[copy.length - 1].value;
        const lastDate = copy[copy.length - 1].date;
        const timediff = ((lastDate - copy[0].date) / (1000 * 60 * 60 * 24)) / 365.25;
        const ytdAnchorRow = copy.filter((e: any) => e.date < dateOfTheYear).slice(-1)[0];
        this.benchmarkReturn = {
          totalReturn: ((lastVal - copy[0].value) / copy[0].value) * 100,
          avgAnn: this.benchmark.annualized_return?.length
            ? this.benchmark.annualized_return.reduce((p: number, c: any) => p + c[1], 0) / this.benchmark.annualized_return.length
            : NaN,
          ytd: ytdAnchorRow && ytdAnchorRow.value !== 0
            ? ((lastVal - ytdAnchorRow.value) / ytdAnchorRow.value) * 100
            : 0,
          cagrY1: timediff > 0 ? (Math.pow(lastVal / copy[0].value, 1 / timediff) - 1) * 100 : 0,
        };
      }
    }

    this.efficientSeries = [];
    const lineData = [];
    const std_dev = [];
    const unconstrainedLineData = [];
    let scatterData = [];
    let optimScatterData = [];
    let benchmarkScatterData = [];
    this.efficientOptimWeights = [];

    if (this.optimizationData && this.optimizationData.optim_weights_efficient_frontier) {
      let weights: any = {};
      this.optimizationData.optim_weights_efficient_frontier.map((data: any) => {
        if (data.view_name === 'forecasted_returns') {
          if (!weights[data.variable]) {
            weights[data.variable] = {
              Ticker: data.variable,
              company_name: this.portfolio.security_overviews[data.variable] ? this.portfolio.security_overviews[data.variable].company_name : this.getFundName(data.variable),
              id_w: [],
              weight: 0,
              vol : std_dev
            }
          }
          weights[data.variable].id_w.push(+(data.value*100).toFixed(2));
        }
      })
      this.efficientOptimWeights = Object.values(weights);
    }

    if (this.portfolio.efficient_frontier) {
      if (this.portfolio.efficient_frontier[this.efficientFrontierType]) {
        let index = 0;
        this.portfolio.efficient_frontier[this.efficientFrontierType].map((d: any) => {
          if (d.view_name === 'forecasted_returns') {
            lineData.push({
              x: +(d.sd_ret*100).toFixed(2),
              y: +(d.exp_ret*100).toFixed(2),
              return_index: index,
              sharpeRatio: d.sharpe,
            })
            std_dev.push(+(d.sd_ret*100).toFixed(2))
            index++;
          }
        })
        this.minVolatility = lineData[0].x;
        this.maxVolatility = lineData[lineData.length-1].x
      }

      if (this.portfolio.efficient_frontier[EfficientType.Unconstrainted]) {
        this.portfolio.efficient_frontier[EfficientType.Unconstrainted].map((d: any) => {
          if (d.view_name === 'forecasted_returns') {
            unconstrainedLineData.push({
              x: +(d.sd_ret*100).toFixed(2),
              y: +(d.exp_ret*100).toFixed(2),
              sharpeRatio: +(d.sharpe).toFixed(2),
            });
          }
        });
      }

      scatterData = this.extractEfficientFrontierScatterData();
      optimScatterData = this.extractEfficientFrontierScatterData(true);

      benchmarkScatterData = this.extractEfficientFrontierBenchmarkScatterData();
    }

    const lineSeries = {
      type: 'line',
      name: 'Optim Efficient Frontier',
      data: lineData,
      zIndex: 3
    }

    const scatterSeries = {
      type: 'scatter',
      name: 'Stocks',
      marker: {
        radius: 4,
        symbol: 'circle'
      },
      data: scatterData,
      zIndex: 10,
      enableMouseTracking: true
    }

    const optimScatterSeries = {
      type: 'scatter',
      name: 'Optims',
      marker: {
        radius: 4,
        symbol: 'circle'
      },
      data: optimScatterData,
      zIndex: 10,
      enableMouseTracking: true
    }

    const unconstrainedLineSeries = {
      type: 'line',
      name: '',
      data: unconstrainedLineData,
      zIndex: 1,
      dashStyle: 'Dash',
      enableMouseTracking: false,
      visible: false
    };

    const benchmarkScatterSeries = {
      type: 'scatter',
      name: 'Benchmark',
      data: benchmarkScatterData,
      zIndex: 10,
      marker: {
        radius: 4,
        symbol: 'circle'
      },
      enableMouseTracking: true,
      visible: false
    }

    this.efficientSeries.push(lineSeries);
    this.efficientSeries.push(scatterSeries);
    this.efficientSeries.push(unconstrainedLineSeries);
    this.efficientSeries.push(optimScatterSeries);
    this.efficientSeries.push(benchmarkScatterSeries);

    this.resetScatterData( );
    // Collect optim weights from optim_weights_efficient_frontier
  }

  
  updatePageWithBenchmark() {
    this.efficientChartLoaded.set(false);
    this.resetScatterData();
  }

  getCorrelationMatrix() {
    this.service.getCorrelationMatrix(this.portfolio.id).subscribe((resp: any) => {
      if (resp.error) {
        return this.service.notificationService.open(resp.error)
      }
      resp.items = resp.items.map((data: any) => { 

         data.Var1 = this.getFundName(data.Var1);
         return data;
       });

      this.correlationMatrixData = this.extractHeatMapData(resp.items)
    })
  }

  getCovarianceMatrix() {
    this.service.getCovarianceMatrix(this.portfolio.id).subscribe((resp: any) => {
      if (resp.error) {
        return this.service.notificationService.open(resp.error)
      }
      if (resp.items) {
        resp.items = resp.items.map((i: any) => {
          i.Var1 = this.getFundName(i.Var1);
          if (!i.converted) {
            i.value = i.value * 100;
            i.converted = true;
          }
          return i;
        })
      }
      this.covarianceMatrixData = this.extractHeatMapData(resp.items);
    })
  }

  saveCorrelation() {
    const params = {
      frequency: this.optimizationData.optim_parameters.correlation_frequency,
      method: this.optimizationData.optim_parameters.correlation_method
    }
    this.service.saveCovmatParams(this.portfolio.id, params).subscribe((resp: any) => {
      if (resp.errro) {
        this.service.notificationService.open(resp.error)
      }
      this.service.notificationService.open("Correlation parameters saved.");
      this.openWebsocket(this.portfolio.id, this.portfolio.name);
    })
  }

  onReturnTypeChange() {
    this.dataLoaded.set(false);
    this.initEfficientChart();
    this.initTabels();
  }

  initTabels() {
    this.optimPerformance = this.portfolio.optim_performance.filter(d => d.view_name === 'forecasted_returns');
    this.optimWeights = this.portfolio.optim_weights.filter(d => d.view_name === 'forecasted_returns');
    this.filteredOptimWeights = [...this.optimWeights];
    setTimeout(() => { this.dataLoaded.set(true); });
  }

  onOptimTypeChange() {
    let prop = 'optim_performance';
    switch (this.selectedPerformanceType) {
      case 'unconstrained':
        prop += '_unconstrained';
        break;
      case 'cost_of_constrained':
        prop += '_cost_of_constrained';
        break;
    }
    this.optimPerformance = this.portfolio[prop].filter(d => d.view_name === 'forecasted_returns');
    this.optimPerformance = this.optimPerformance.map(p => {
      p.name = this.service.convertEvaluationType(p.Portfolio.split('_').join(' '));
      return p;
    });
  }

  onCalculateReturnsClick() {

    if (
      !this.optimizationData.optim_parameters.return_start_date ||
      !this.optimizationData.optim_parameters.return_end_date
    ) {
      return this.service.notificationService.open('Fill the start and end dates.')
    }
    this.handleReturnCalculationRequest('Your optimization is running');
  }

  collectParamsForPost() {
    
    let optimParams: any = {
      optim_parameters: this.optimizationData.optim_parameters
    }

    let assumptions = [];
    this.optimizationData.forecasted_returns.map((ass:any) => {
      assumptions.push({
        forecasted_returns:  ass.forecasted_returns,  // divide with 100 to save proper data in db
        Ticker: ass.Ticker
      });
    })
    optimParams.optim_parameters.optim_performance = this.optim;
    optimParams.optim_parameters.assumptions = assumptions;
    optimParams.status = 1;
    return optimParams
  }

  onOptimizeClick(saveCorrelation = false, saveInputsOnly = false) {
    let optimParams: any = this.collectParamsForPost();

    // Trigger optimization R script if we click the optimize button (saveCorrelation will be true if reload correlation clicked)
    if (!saveCorrelation) {
      optimParams.optim_parameters.action = 'optimize' // trigger calculation
    }
    this.service.saveOptimizationData(this.portfolio.id, optimParams).subscribe((resp: any) => {
      if (resp.errro) {
        this.service.notificationService.open(resp.error)
      }

      if (!saveInputsOnly) {
        if (saveCorrelation) {
          this.saveCorrelation();
        } else {
          this.openWebsocket(this.portfolio.id, this.portfolio.name);
        }
      }
    })
  }

  onSliderChanged(event: any) {
    this.sliderVolatility = event;
  }

  // Fired after the Next Best Trade apply flow refetches optimizationData.
  // Re-emitting on optim$ runs reloadPf, which refreshes optimName (header),
  // the optim_performance tables and the efficient frontier chart against the
  // freshly loaded modified_optim_weights.
  onOptimizationDataRefreshed(data: any) {
    if (!data) return;
    this.optimizationData = data;
    const newOptim = data.optim_parameters?.optim_performance;
    if (newOptim) {
      this.service.changePfOptim(newOptim);
    }
  }

  onFilterChange() {
    this.viewTablesLoaded.set(false);
    if (this.filter.type && this.filter.type.length) {
      this.filteredOptimWeights = [...this.optimWeights].filter(d => this.filter.type.includes(this.portfolio.security_overviews[d.Ticker].security_type_equity));
      this.filteredHistoricalReturnTableData = [...this.historicalReturnTableData].filter(d => this.filter.type.includes(this.portfolio.security_overviews[d.Ticker].security_type_equity));
      this.filteredMarketEquilibriumTableData = [...this.marketEquilibriumTableData].filter(d => this.filter.type.includes(this.portfolio.security_overviews[d.Ticker].security_type_equity));
      this.filteredImpliedReturnData = [...this.impliedReturnData].filter(d => this.filter.type.includes(this.portfolio.security_overviews[d.Ticker].security_type_equity));
    } else {
      this.filteredOptimWeights = [...this.optimWeights];
      this.filteredHistoricalReturnTableData = [...this.historicalReturnTableData];
      this.filteredMarketEquilibriumTableData = [...this.marketEquilibriumTableData];
      this.filteredImpliedReturnData = [...this.impliedReturnData];
    }
    this.filteredForecastedReturnsTableData = this.forecastedReturnsTableData.map((e) => {
      return {
        ...e,
        mkt_eq_returns: (e.implied_returns - e.market_equilibrium).toPrecision(4),
      }
    }).filter((d: any) => {
      let sectorFound = true;
      let countryFound = true;
      let typeFound = true;

      if (this.filter.sector && this.filter.sector.length) {
        sectorFound = this.filter.sector.includes(d.sector);
      }

      if (this.filter.country && this.filter.country.length) {
        countryFound = this.filter.country.includes(d.country);
      }

      if (this.filter.type && this.filter.type.length) {
        typeFound = this.filter.type.includes(d.type);
      }

      if (sectorFound && countryFound && typeFound) {
        return d;
      }
    })
    setTimeout(() => { this.viewTablesLoaded.set(true); });
  }

  onOptimizedWeightSaved() {
    this.openWebsocket(this.portfolio.id, this.portfolio.name);
  }

  triggerCalc(type: string) {
    const minValue = new Date();
    switch(type) {
      case 'ytd':
        minValue.setMonth(0);
        minValue.setDate(1);
        this.optimizationData.optim_parameters.return_start_date = minValue.toISOString().split('T')[0];
        break;
      case '1':
        minValue.setFullYear(minValue.getFullYear() - 1);
        this.optimizationData.optim_parameters.return_start_date = minValue.toISOString().split('T')[0];
        break;
      case '5':
        minValue.setFullYear(minValue.getFullYear() - 5);
        this.optimizationData.optim_parameters.return_start_date = minValue.toISOString().split('T')[0];
        break;
        case '10':
          minValue.setFullYear(minValue.getFullYear() - 10);
          this.optimizationData.optim_parameters.return_start_date = minValue.toISOString().split('T')[0];
          break;
        case '15':
          minValue.setFullYear(minValue.getFullYear() - 15);
          this.optimizationData.optim_parameters.return_start_date = minValue.toISOString().split('T')[0];
          break;
      case 'start':
        this.optimizationData.optim_parameters.return_start_date = this.portfolio.start_date;
        break;
      default:
        break;
    }
    this.optimizationData.optim_parameters.return_end_date = (new Date()).toISOString().split('T')[0];
  }

  getConstraintForAsset(asset: string) {
    let result = null;
    this.optimizationData.group_constraints.forEach(function (constraint) {
      if (constraint.type.id === 'asset' && constraint.items[0].name === asset) {
        result = {
          min: constraint.min / 100,
          max: constraint.max / 100
        }
      }
    })

    return result;
  }

  handleCalculate(event: any) {
    let optimParams: any = this.collectParamsForPost();
    optimParams.optim_parameters.action = 'risk-analysis' // trigger calculation
    // collectParamsForPost() already set optim_performance from this.optim.
    // For the "Saved Custom Trade" view we want the backend to run against the
    // saved Next Best Trade weights, so keep that value untouched. For every
    // other caller (slider / lock toggle) we fall back to the target-return flow.
    if (optimParams.optim_parameters.optim_performance !== 'next_best_trade') {
      optimParams.optim_parameters.optim_performance = 'efficient_portfolio_given_mu'
      optimParams.optim_parameters.efficient_target_return = event.return;
    }

    this.service.saveOptimizationData(this.portfolio.id, optimParams).subscribe((resp: any) => {
      if (resp.errro) {
        this.service.notificationService.open(resp.error)
        return;
      }
      this.service.notificationService.open("Parameters saved, go to risk analysis after 10 seconds to see the result");
      this.portfolio.needReload = true;
    })
  }

  exportCovarianceCorrelationChart() {
    const element = document.querySelector('#covariance-correlation-chart') as HTMLElement;
    this.exportFullHeatmapTable(
      'covarianceCorrelation',
      element,
      `covariance-correlation-${this.portfolio?.name || 'export'}.jpeg`
    );
  }

  exportImpliedViewsChart() {
    const element = document.querySelector('#implied-views-section-optimized') as HTMLElement;
    this.handleCardExport('impliedViews', element, `implied-views-${this.portfolio?.name || 'export'}.jpeg`);
  }

  exportMyViewsChart() {
    const element = document.querySelector('#my-views-section-optimized') as HTMLElement;
    this.handleCardExport('myViews', element, `my-views-${this.portfolio?.name || 'export'}.jpeg`);
  }
  exportCombinedReturnsChart() {
    const element = document.querySelector('#combined-returns-section-optimized') as HTMLElement;
    this.handleCardExport('combinedReturns', element, `combined-returns-${this.portfolio?.name || 'export'}.jpeg`);
  }
  exportConstraintsChart() {
    const element = document.querySelector('#constraints-section-optimized') as HTMLElement;
    this.handleCardExport('constraints', element, `constraints-${this.portfolio?.name || 'export'}.jpeg`);
  }

  private async exportFullHeatmapTable(key: string, element: HTMLElement | null, filename: string): Promise<void> {
    if (!element) {
      this.service?.notificationService?.open('Card not found. Please try again.');
      return;
    }

    this.cardExporting[key] = true;
    const exportWrapper = document.createElement('div');
    exportWrapper.style.position = 'fixed';
    exportWrapper.style.left = '-10000px';
    exportWrapper.style.top = '0';
    exportWrapper.style.zIndex = '-1';
    exportWrapper.style.background = '#ffffff'; // intentional: PDF/image export always uses white canvas
    exportWrapper.style.padding = '24px';
    exportWrapper.style.width = 'max-content';

    const clonedElement = element.cloneNode(true) as HTMLElement;
    clonedElement.removeAttribute('id');
    clonedElement.style.width = 'max-content';
    clonedElement.style.maxWidth = 'none';
    this.prepareHeatmapExportClone(clonedElement);

    exportWrapper.appendChild(clonedElement);
    document.body.appendChild(exportWrapper);

    try {
      await this.exportService.exportElementAsImage(clonedElement, {
        filename,
        scale: 1.5
      });
    } finally {
      document.body.removeChild(exportWrapper);
      this.cardExporting[key] = false;
    }
  }

  private prepareHeatmapExportClone(root: HTMLElement): void {
    const tableContainers = root.querySelectorAll('.heatmap-table-container') as NodeListOf<HTMLElement>;
    tableContainers.forEach((container) => {
      container.style.maxHeight = 'none';
      container.style.height = 'auto';
      container.style.overflow = 'visible';
      container.style.width = 'max-content';
    });

    const tables = root.querySelectorAll('.heatmap-table') as NodeListOf<HTMLElement>;
    tables.forEach((table) => {
      table.style.width = 'auto';
    });

    const stickyElements = root.querySelectorAll('.sticky-left, .sticky-col-top, .sticky-corner') as NodeListOf<HTMLElement>;
    stickyElements.forEach((sticky) => {
      sticky.style.position = 'static';
      sticky.style.top = 'auto';
      sticky.style.left = 'auto';
      sticky.style.zIndex = 'auto';
    });
  }

  onExportPdf(): void {
    let targetPortfolio = 'efficient_portfolio_max_sharpe_ratio';

    if (this.optim) {
      targetPortfolio = this.optim.toString();
    } else if (
      this.optimizationData &&
      this.optimizationData.optim_parameters &&
      this.optimizationData.optim_parameters.optim_performance
    ) {
      targetPortfolio = this.optimizationData.optim_parameters.optim_performance;
    }

    this.dialog.open(PdfExportDialogComponent, {
      data: {
        portfolioId: this.portfolio.id,
        portfolioName: this.portfolio.name,
        targetPortfolio,
      },
      maxWidth: '1200px',
      minWidth: '1100px',
    });
  }
}
